import { Thermometer, Droplets, EggFried } from 'lucide-react';
import { EggSettings } from './EggTimer';
import { cn } from '../../utils/cn';

interface EggSettingsSelectorProps {
  settings: EggSettings;
  onChange: (settings: Partial<EggSettings>) => void;
  disabled?: boolean;
}

const EggSettingsSelector = ({ 
  settings, 
  onChange,
  disabled = false
}: EggSettingsSelectorProps) => {
  return (
    <div className="w-full bg-white rounded-xl shadow-md p-6">
      <h3 className="font-display text-xl text-gray-800 font-semibold mb-4">
        Customize Your Settings
      </h3>
      
      <div className="space-y-6">
        {/* Egg Temperature */}
        <div>
          <div className="flex items-center mb-2">
            <Thermometer className="w-5 h-5 text-primary-500 mr-2" />
            <h4 className="font-medium text-gray-700">Egg Temperature</h4>
          </div>
          
          <div className="flex space-x-4">
            <button
              className={cn(
                "flex-1 py-2 px-4 rounded-md border text-center transition-colors",
                settings.temperature === 'refrigerated'
                  ? "bg-primary-50 border-primary-300 text-primary-700" 
                  : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50",
                disabled && "opacity-60 cursor-not-allowed"
              )}
              onClick={() => !disabled && onChange({ temperature: 'refrigerated' })}
              disabled={disabled}
            >
              Refrigerated
            </button>
            <button
              className={cn(
                "flex-1 py-2 px-4 rounded-md border text-center transition-colors",
                settings.temperature === 'room'
                  ? "bg-primary-50 border-primary-300 text-primary-700" 
                  : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50",
                disabled && "opacity-60 cursor-not-allowed"
              )}
              onClick={() => !disabled && onChange({ temperature: 'room' })}
              disabled={disabled}
            >
              Room Temperature
            </button>
          </div>
        </div>
        
        {/* Egg Size */}
        <div>
          <div className="flex items-center mb-2">
            <EggFried className="w-5 h-5 text-primary-500 mr-2" />
            <h4 className="font-medium text-gray-700">Egg Size</h4>
          </div>
          
          <div className="flex space-x-4">
            <button
              className={cn(
                "flex-1 py-2 px-4 rounded-md border text-center transition-colors",
                settings.size === 'small'
                  ? "bg-primary-50 border-primary-300 text-primary-700" 
                  : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50",
                disabled && "opacity-60 cursor-not-allowed"
              )}
              onClick={() => !disabled && onChange({ size: 'small' })}
              disabled={disabled}
            >
              Small
            </button>
            <button
              className={cn(
                "flex-1 py-2 px-4 rounded-md border text-center transition-colors",
                settings.size === 'medium'
                  ? "bg-primary-50 border-primary-300 text-primary-700" 
                  : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50",
                disabled && "opacity-60 cursor-not-allowed"
              )}
              onClick={() => !disabled && onChange({ size: 'medium' })}
              disabled={disabled}
            >
              Medium
            </button>
            <button
              className={cn(
                "flex-1 py-2 px-4 rounded-md border text-center transition-colors",
                settings.size === 'large'
                  ? "bg-primary-50 border-primary-300 text-primary-700" 
                  : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50",
                disabled && "opacity-60 cursor-not-allowed"
              )}
              onClick={() => !disabled && onChange({ size: 'large' })}
              disabled={disabled}
            >
              Large
            </button>
          </div>
        </div>
        
        {/* Cooking Method */}
        <div>
          <div className="flex items-center mb-2">
            <Droplets className="w-5 h-5 text-primary-500 mr-2" />
            <h4 className="font-medium text-gray-700">Cooking Method</h4>
          </div>
          
          <div className="flex space-x-4">
            <button
              className={cn(
                "flex-1 py-2 px-4 rounded-md border text-center transition-colors",
                settings.cookingMethod === 'boiling'
                  ? "bg-primary-50 border-primary-300 text-primary-700" 
                  : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50",
                disabled && "opacity-60 cursor-not-allowed"
              )}
              onClick={() => !disabled && onChange({ cookingMethod: 'boiling' })}
              disabled={disabled}
            >
              Boiling Water
            </button>
            <button
              className={cn(
                "flex-1 py-2 px-4 rounded-md border text-center transition-colors",
                settings.cookingMethod === 'cold'
                  ? "bg-primary-50 border-primary-300 text-primary-700" 
                  : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50",
                disabled && "opacity-60 cursor-not-allowed"
              )}
              onClick={() => !disabled && onChange({ cookingMethod: 'cold' })}
              disabled={disabled}
            >
              Cold Water
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EggSettingsSelector;